import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'antd';
import moment from 'moment';
import Loadable from '@/components/common/loadable';
import { getOrders, getReferenceData } from '@/store/actions/order';
import IconExpand2 from '@/assets/images/transaction/row-expand-2.png';
import IconExpand1 from '@/assets/images/transaction/row-expand-1.png';
import IconExpand3 from '@/assets/images/transaction/row-expand-3.png';
import IconExpand4 from '@/assets/images/transaction/row-expand-4.png';
import PlusIcon from '@/assets/images/transaction/icons-plus.svg';
import ExpandIcon from '@/assets/images/transaction/icons-minus.svg';
import { toMoney } from '@/common/utils';
import HeadingCenter from '@/components/common/heading-center';
import { orderListPage } from '@/components/common/heading-center/breadcrumb-config';
import OrderDetail from '../detail';
import './index.scss';
import { PAGINATION_DEFAULT } from '@/common/constant';

const BaseTable = Loadable({
  loader: () => import('@/components/base-table'),
});

const defaultQuery = PAGINATION_DEFAULT;
const OrderList = () => {
  const dispatch = useDispatch();
  const { list: orderList, isLoading, orderStatus } = useSelector((state) => state.order);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [expandedKeysAll, setExpandedKeysAll] = useState(false);
  const [isOpenModalDetail, setOpenModalDetail] = useState(false);
  const [filter, setFilter] = useState(defaultQuery);

  /**
   * Call API get categories with params
   * @param {*} params
   */
  const getOrdersFunc = (params) => {
    setFilter(params);
    dispatch(getOrders(params));
  };

  useEffect(() => {
    getOrdersFunc(defaultQuery);
    dispatch(getReferenceData({}));
  }, []);

  /**
   * Handle logic for order list
   * @param {*} rows
   * @returns
   */
  const handleOrderList = (rows) => {
    const elDays = {};
    const setExpandKeys = [];
    rows.forEach((el) => {
      const date = moment(el.created_at).format('YYYY-MM-DD');
      if (!elDays[date]) {
        const key = Math.random();
        setExpandKeys.push(key);
        elDays[date] = [
          {
            dayAggregate: moment(el.created_at).format('dddd, DD MMMM YYYY'),
            cashAmount: 0,
            cardAmount: 0,
            masterPassAmount: 0,
            totalOrder: 0,
            total_amount: 0,
            id: key,
            isExpand: true,
          },
        ];
      }

      elDays[date].push({
        ...el,
        orderTime: moment(el.created_at).format('hh:mm:ss'),
      });
    });
    setExpandedKeysAll(setExpandKeys);
    let nData = [];
    Object.keys(elDays).forEach((key) => {
      nData = [...nData, ...elDays[key]];
    });
    return nData;
  };

  const data = useMemo(() => {
    if (orderList.data.length) return handleOrderList(orderList.data);
    return [];
  }, [orderList]);

  /**
   * Handle expand & collapse all
   * @param {*} isExpand
   */
  const expandAndCollapseAll = (isExpand) => {
    if (isExpand) return setExpandedKeys(expandedKeysAll);
    return setExpandedKeys([]);
  };

  /**
   * Search order by name
   * @param {*} value
   */
  const onSearch = (value = '') => {
    getOrdersFunc({ search: value || undefined, ...defaultQuery });
  };

  /**
   * The function check keys in row to expand row
   * @param {*} param0
   * @returns
   */
  const onRow = ({ id, isExpand }) => ({
    className: expandedKeys.includes(id) ? ' expand-item-parent' : '',
    onClick: () => {
      if (!isExpand) setOpenModalDetail(true);
    },
  });

  const onCloseModalDetail = () => {
    setOpenModalDetail(false);
  };

  /**
   * Expand item
   * @param {*} expanded
   * @param {*} param1
   */
  const onExpandItem = (expanded, { id }) => {
    const nExpandedKeys = expanded ? expandedKeys.concat(id) : expandedKeys.filter((k) => k !== id);
    setExpandedKeys(nExpandedKeys);
  };

  const columns = [
    {
      title: 'Date / Time',
      dataIndex: 'orderTime',
      align: 'left',
      width: '18%',
      render: (val, row) => (row.dayAggregate ? row.dayAggregate : val),
    },
    {
      title: 'Order ID',
      dataIndex: 'id',
      align: 'left',
      width: '12%',
      render: (val, row) => (row.isExpand ? null : val),
    },
    {
      title: 'Status',
      dataIndex: 'status_id',
      align: 'left',
      render: (val) => {
        const status = orderStatus.find(({ id }) => id === val);
        return status ? status.name : null;
      },
    },
    {
      title: 'Method',
      dataIndex: 'payment_method_type',
      align: 'left',
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      align: 'left',
      render: (val, { user_first_name, user_last_name }) =>
        user_first_name || user_last_name ? (
          <div>{`${user_first_name} ${user_last_name}`}</div>
        ) : null,
    },
    {
      title: 'Invoice',
      dataIndex: 'customer_name',
      align: 'center',
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      align: 'center',
      render: (val) => toMoney(val),
    },
  ];

  return (
    <div className="transaction-list">
      <HeadingCenter pageConfig={orderListPage} isSearch />
      <BaseTable
        onSearch={onSearch}
        tableName="order"
        columns={columns}
        data={[...data]}
        total={orderList?.metadata?.total}
        metadata={filter}
        isLoading={isLoading}
        toggleExpandAllProp={expandAndCollapseAll}
        onChangePageProps={getOrdersFunc}
        isFilter={false}
        expandable={{
          expandedRowKeys: expandedKeys,
          expandedRowRender: () => <ExpandRowRender />,
          rowExpandable: (record) => record.dayAggregate,
          expandIcon: ({ expanded, onExpand, record }) => {
            if (!record.dayAggregate) return null;
            return expanded ? (
              <img
                src={ExpandIcon}
                alt="edit icon"
                className="icon-expand"
                onClick={(e) => onExpand(record, e)}
              />
            ) : (
              <img
                src={PlusIcon}
                alt="edit icon"
                className="icon-expand"
                onClick={(e) => onExpand(record, e)}
              />
            );
          },
        }}
        onExpand={onExpandItem}
        onRow={onRow}
        rowClassName={(record) => (record.dayAggregate ? 'data-row-aggregate' : '')}
        isFixed={false}
      />
      <OrderDetail isOpen={isOpenModalDetail} onCloseProp={onCloseModalDetail} />
    </div>
  );
};

const ExpandRowRender = ({ cashAmount, cardAmount, masterPassAmount, totalOrder }) => (
  <div className="expand-row">
    <Row gutter={[16, 24]}>
      <Col span={6}>
        <Row className="ant-col-item">
          <Col xs={16} xl={6} className="item-icon">
            <img src={IconExpand1} alt="icon" />
          </Col>
          <Col xs={16} xl={12} className="content">
            <p className="price">{toMoney(cashAmount)}</p>
            <p>Cash Order</p>
          </Col>
        </Row>
      </Col>
      <Col span={6}>
        <Row className="ant-col-item">
          <Col sm={16} xl={6} className="item-icon">
            <img src={IconExpand2} alt="icon" />
          </Col>
          <Col sm={16} xl={12} className="content">
            <p className="price">{toMoney(cardAmount)}</p>
            <p>Card Order</p>
          </Col>
        </Row>
      </Col>
      <Col span={6}>
        <Row className="ant-col-item">
          <Col sm={16} xl={6} className="item-icon">
            <img src={IconExpand3} alt="icon" />
          </Col>
          <Col sm={16} xl={12} className="content">
            <p className="price">{toMoney(masterPassAmount)}</p>
            <p>Masterpass Order</p>
          </Col>
        </Row>
      </Col>
      <Col span={6}>
        <Row className="ant-col-item">
          <Col sm={16} xl={6} className="item-icon">
            <img src={IconExpand4} alt="icon" />
          </Col>
          <Col sm={16} xl={12} className="content">
            <p className="price">{toMoney(totalOrder)}</p>
            <p>Total Order</p>
          </Col>
        </Row>
      </Col>
    </Row>
  </div>
);

export default OrderList;
